import React from 'react';
import tabbable from 'tabbable';
import _ from 'underscore';

import {
  CardPositionContext,
  ContentInfoContext,
} from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ConnectedContentCardWrapper } from '~src/spacesPlatform/components/card/ConnectedContentCardWrapper';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { ShowMoreCardsAction } from '../actions/ShowMoreCardsAction';
import { ChannelStyles } from '../styles/ChannelStyles';
import { ChannelTypes } from '../types/ChannelTypes';
import { Channel } from './Channel';

export interface ContentInfoChannelBaseProps {
  readonly channelType: ChannelTypes;
  readonly title: string;
  readonly list: ReadonlyArray<IContentInfo>;
  readonly seeAllUrl: string;
  readonly seeAllAltText: string;
  readonly showLastAccessedAt?: boolean;
  readonly isFetching: boolean;
  readonly numRows: number;
  readonly spaceInfo: SpaceInfo;
  readonly shouldListWrapVertically?: boolean;
}

export interface ContentInfoChannelOwnProps {
  readonly maxCardsPerRow: number;
  readonly channelType?: ChannelTypes;
  readonly numCardsToShow?: number;
  readonly title?: string;
  readonly headerIconPath?: string;
  readonly headerIconNode?: React.ReactNode;
  readonly showMoreCards?: (numCardsToShow: number, channelType: ChannelTypes) => ShowMoreCardsAction;
}

export type ContentInfoChannelProps = ContentInfoChannelBaseProps & ContentInfoChannelOwnProps;

export class ContentInfoCardChannel extends React.PureComponent<ContentInfoChannelProps> {

  /**
   * Rendered slot index of the card the user last activated (its "Remove from Favorites"
   * toggle, or the "..." actions menu that leads to Delete). Recorded at the click/keydown
   * EVENT — never inferred from a later list-length diff, which would steal focus on unrelated
   * re-renders (resize maxNumCards recompute, "show more/less", another channel's refetch).
   */
  private pendingFocusRestore: { contentId: string, slot: number } | undefined = undefined;

  /**
   * Wrapper <div> of each rendered card, keyed by content id, so focus restoration can target a
   * surviving card through a ref this component owns — rather than reaching into the child Channel's
   * private DOM via findDOMNode + querySelector('ul').
   */
  private cardRefs: Map<string, HTMLDivElement> = new Map();

  private setCardRef = (contentId: string) => (node: HTMLDivElement | null) => {
    if (node) {
      this.cardRefs.set(contentId, node);
    } else {
      this.cardRefs.delete(contentId);
    }
  };

  private registerFocusRestore = (contentId: string, slot: number) => {
    this.pendingFocusRestore = { contentId, slot };
  };

  private handleCardKeyDownCapture = (contentId: string, slot: number) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    // FavoriteToggle is a role="button" span with its own onKeyDown; Enter/Space is its
    // activation, so register on the same keys rather than relying on a synthesized click.
    if (e.key === 'Enter' || e.key === ' ') {
      this.registerFocusRestore(contentId, slot);
    }
  };

  public componentDidUpdate(_prevProps: ContentInfoChannelProps) {
    // WCAG 2.4.3 (W-21968122): when the card the user acted on is removed, its DOM node
    // unmounts and focus falls to document.body. Move focus to the card now occupying that
    // slot. `pendingFocusRestore` (the restore intent recorded at the click/keydown event) is
    // keyed to the acted-on content id and consumed only once that id has actually left the list
    // AND focus was genuinely lost, so an unrelated re-render never repositions focus and an async
    // full refetch that backfills the list length still restores. Keying by id (not a list-length
    // diff) also keeps concurrent removals independent.
    if (this.pendingFocusRestore === undefined) {
      return;
    }
    const { contentId, slot } = this.pendingFocusRestore;
    const stillPresent = this.props.list.some((item) => item.id === contentId);
    if (stillPresent) {
      return; // removal has not happened yet; keep the recorded restore intent
    }
    this.pendingFocusRestore = undefined;
    const activeElement = document.activeElement;
    const focusLost = !activeElement || activeElement === document.body ||
      activeElement === document.documentElement || !document.body.contains(activeElement);
    if (focusLost && this.props.list.length > 0) {
      this.focusCardAtSlot(slot);
    }
  }

  private focusCardAtSlot = (removedIndex: number) => {
    const renderedList = this.props.list.slice(0, this.calculateMaxNumCards());
    if (renderedList.length === 0) {
      return; // channel emptied; nothing to focus here
    }
    const target = this.cardRefs.get(renderedList[Math.min(removedIndex, renderedList.length - 1)].id);
    if (!target) {
      return;
    }
    const focusables = tabbable(target);
    if (focusables.length > 0) {
      focusables[0].focus(); // first tabbable = the card's thumbnail link ("next element in tab order")
    }
  };

  calculateMaxNumCards = () => {
    let maxNumCards: number;
    if (this.props.numCardsToShow > 0) {
      // If numCardsToShow was passed as > 0, show that many cards
      maxNumCards = this.props.numCardsToShow;
    } else {
      // If numCardsToShow wasn't passed or is passed as 0, use the full grid calculation
      maxNumCards = this.props.maxCardsPerRow * this.props.numRows;
    }
    return maxNumCards;
  };

  render() {
    const maxNumCards = this.calculateMaxNumCards();

    let cards = this.props.list.slice(0, maxNumCards).map((contentInfo: IContentInfo, id: number) => {
        return (
          <li key={`${contentInfo.contentType}:${contentInfo.id}:${this.props.channelType}`}>
            <ContentInfoContext.Provider value={contentInfo}>
              <CardPositionContext.Provider value={id + 1}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  ref={this.setCardRef(contentInfo.id)}
                  className={ChannelStyles.cardDimensions}
                  onClickCapture={() => this.registerFocusRestore(contentInfo.id, id)}
                  onKeyDownCapture={this.handleCardKeyDownCapture(contentInfo.id, id)}
                >
                  <ConnectedContentCardWrapper
                    content={contentInfo}
                    showActionMenu={true}
                    showLastAccessedAt={this.props.showLastAccessedAt}
                    forDragPreview={false}
                    dragInProgress={false}
                    setIsDragging={_.noop}
                    reorderItemAction={_.noop}
                    allowDragAndDrop={false}
                  />
                </div >
              </CardPositionContext.Provider>
            </ContentInfoContext.Provider>
          </li>
        );
    });

    return (
      <Channel
        numCards={maxNumCards}
        headerIconNode={this.props.headerIconNode}
        {...this.props}
      >
        {cards}
      </Channel>
    );
  }
}
